/* eslint-disable no-param-reassign */
import {
  CustomColumnItem,
  ID,
  LineItem,
  LineItemChanges,
  LineItemsState,
  LotChanges,
} from 'types';
import {
  isCustomColumnItemChanged,
  isLineItemChanged,
  matchesNew,
  updateChanges,
} from '@components/bid_request/details/utils';
import { getOriginalCustomColumnItem, getOriginalLineItem } from './getOriginalFromState';

/**
 * Helper function to ensure lot structure exists in changes
 */
export const ensureLotStructure = (draft: LineItemsState, lotId: ID): void => {
  draft.changes = draft.changes || ({} as Record<ID, LotChanges>);
  draft.changes[lotId] = draft.changes[lotId] || ({ id: lotId } as LotChanges);
  draft.changes[lotId].line_items = draft.changes[lotId].line_items || ({} as Record<ID, LineItemChanges>);
};

/**
 * Helper function to process line item changes
 */
export const processLineItemChanges = (
  draft: LineItemsState,
  lotId: ID,
  lineItems: Record<ID, Partial<LineItem>>
): void => {
  if (!lineItems || !Object.keys(lineItems).length) return;

  const lotChanges = draft.changes[lotId];
  if (!lotChanges?.line_items) return;

  Object.keys(lineItems).forEach((key: ID): void => {
    const id = matchesNew(key) ? key : Number(key);
    const updates = lineItems[id];

    if (!updates || !lotChanges.line_items) return;

    // Update the line item with changes
    lotChanges.line_items[id] = updateChanges({
      changes: lotChanges.line_items[id] || ({ id, lot_id: lotId } as LineItemChanges),
      original: getOriginalLineItem({ lineItemId: id, lotId, lots: draft.originalLots }),
      updates,
    }) as LineItemChanges;

    // Removes custom line item from change list if no changes were made to its fields
    const isChanged = isLineItemChanged({ lineItemId: id, lotId, state: draft });
    if (!isChanged && lotChanges.line_items) {
      delete lotChanges.line_items[id];
    }
  });
};

/**
 * Helper function to process custom column item changes
 */
export const processCustomColumnItemChanges = (
  draft: LineItemsState,
  lotId: ID,
  customColumnItems: Record<ID, Partial<CustomColumnItem>>
): void => {
  if (!customColumnItems || !Object.keys(customColumnItems).length) return;

  const lotChanges = draft.changes[lotId];
  if (!lotChanges?.line_items) return;

  Object.keys(customColumnItems).forEach((key: ID): void => {
    const id = matchesNew(key) ? key : Number(key);
    const updates = customColumnItems[id];

    if (!updates?.line_item_id || !lotChanges.line_items) return;

    const itemId = updates.line_item_id;
    const original = getOriginalCustomColumnItem({
      ccItemId: id,
      lineItemId: itemId,
      lotId,
      lots: draft.originalLots
    });

    // Ensure nested structure exists for this line item
    lotChanges.line_items[itemId] = lotChanges.line_items[itemId] || ({} as LineItemChanges);
    const lineItemChanges = lotChanges.line_items[itemId];
    if (lineItemChanges) {
      lineItemChanges.custom_column_items = lineItemChanges.custom_column_items || ({} as Record<ID, CustomColumnItem>);

      if (lineItemChanges.custom_column_items) {
        // Update the custom column item
        lineItemChanges.custom_column_items[id] = updateChanges({
          changes: lineItemChanges.custom_column_items[id] || ({ id, line_item_id: itemId } as CustomColumnItem),
          original,
          updates,
        }) as CustomColumnItem;

        // Removes custom column item from change list if no changes were made to its fields
        const isChanged = isCustomColumnItemChanged({
          customColumnItemId: id,
          lineItemId: itemId,
          lotId,
          state: draft,
        });

        if (!isChanged) {
          delete lineItemChanges.custom_column_items[id];
        }
      }
    }
  });
};
